import { useTable } from '@refinedev/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/format';
import type { AuditEntry } from '@/lib/types';

const ALL = '__all__';

const ACTIONS = [
  'account_created',
  'account_imported',
  'credentials_revealed',
  'recovery_code_assigned',
  'recovery_code_unassigned',
  'recovery_code_marked_used',
  'recovery_code_marked_lost',
  'status_changed',
  'topup_applied',
  'note_added',
  'suspended',
  'banned',
  'retired',
] as const;

const ACTOR_TYPES = ['admin', 'automation', 'system'] as const;

export function AuditListPage() {
  const [actionFilter, setActionFilter] = useState<string>(ALL);
  const [actorFilter, setActorFilter] = useState<string>(ALL);
  const [accountIdFilter, setAccountIdFilter] = useState('');

  const { tableQueryResult, current, setCurrent, pageSize, setFilters } = useTable<AuditEntry>({
    resource: 'audit-log',
    pagination: { current: 1, pageSize: 50 },
  });

  const applyFilters = () => {
    const filters = [];
    if (actionFilter !== ALL)
      filters.push({ field: 'action', operator: 'eq' as const, value: actionFilter });
    if (actorFilter !== ALL)
      filters.push({ field: 'actor_type', operator: 'eq' as const, value: actorFilter });
    if (accountIdFilter.trim())
      filters.push({ field: 'account_id', operator: 'eq' as const, value: accountIdFilter.trim() });
    setFilters(filters, 'replace');
    setCurrent(1);
  };

  const data = tableQueryResult.data?.data ?? [];
  const total = tableQueryResult.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Auditoria</h1>
        <p className="text-muted-foreground">
          Histórico de ações em contas — {total} entradas no total.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label className="text-xs uppercase text-muted-foreground">Ação</label>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Todas ações" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas ações</SelectItem>
              {ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase text-muted-foreground">Ator</label>
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {ACTOR_TYPES.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase text-muted-foreground">UUID da conta</label>
          <Input
            className="w-72 font-mono text-sm"
            placeholder="00000000-0000-..."
            value={accountIdFilter}
            onChange={(e) => setAccountIdFilter(e.target.value)}
          />
        </div>
        <Button variant="secondary" onClick={applyFilters}>
          Aplicar
        </Button>
        {(actionFilter !== ALL || actorFilter !== ALL || accountIdFilter) && (
          <Button
            variant="ghost"
            onClick={() => {
              setActionFilter(ALL);
              setActorFilter(ALL);
              setAccountIdFilter('');
              setFilters([], 'replace');
              setCurrent(1);
            }}
          >
            Limpar
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Data</TableHead>
              <TableHead className="w-56">Ação</TableHead>
              <TableHead className="w-24">Ator</TableHead>
              <TableHead>Conta</TableHead>
              <TableHead>Metadata</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableQueryResult.isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Nenhuma ação registrada.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(row.created_at)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.action}</TableCell>
                  <TableCell>
                    <Badge variant="muted">{row.actor_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/contas/${row.account_id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {row.account_id.slice(0, 8)}…
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-md truncate font-mono text-xs text-muted-foreground">
                    {Object.keys(row.metadata ?? {}).length > 0
                      ? JSON.stringify(row.metadata)
                      : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Página {current} de {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={current <= 1}
            onClick={() => setCurrent(current - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={current >= totalPages}
            onClick={() => setCurrent(current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
